import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { IoIosHeartDislike } from 'react-icons/io';
import { IoCheckmarkCircle, IoPersonRemove, IoTime } from 'react-icons/io5';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HubIcon from '@mui/icons-material/Hub';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { ListItemText, Menu, MenuItem, Stack, Tooltip } from '@mui/material';

import { useUserConnections } from '../hooks/useUserConnections';
import { useUserRelationship } from '../hooks/useUserRelationship';
import {
  createEdge,
  getEdgeTypes,
  getEdges,
  getUserStats,
  removeEdge,
} from '../services/edgeService';
import Button from 'components/Button';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import Scheduling from 'pages/AgendaConfiguration/components/Scheduling';
import { useIsMobile } from 'utils/mediaQueries';

interface Props {
  loggedUserId: string;
  selectedUserId: string;
  hostName?: string;
}

type EdgeType =
  | 'FAVORITED'
  | 'LIKE'
  | 'DISLIKE'
  | 'CONNECTION'
  | 'SCHEDULED'
  | 'WHATSAPP'
  | 'APPLAUSE'
  | 'ENCOURAGE';

interface Edge {
  id: string;
  type_code: EdgeType;
}

interface EdgeAction {
  type: string;
  icon: React.ReactElement;
  iconActive?: React.ReactElement;
  label: string;
  tooltip: string;
  variant: 'primary' | 'outline';
  show: boolean | undefined | number;
}

const UserActionsMenu: React.FC<Props> = ({ loggedUserId, selectedUserId, hostName = '' }) => {
  const { toastError, toastSuccess, toastInfo } = useToast();
  const isMobile = useIsMobile();
  const theme = useThemeContext();
  const queryClient = useQueryClient();
  const isDev = process.env.NODE_ENV === 'development';

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [edgeTypes, setEdgeTypes] = useState<any[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedEdges, setSelectedEdges] = useState<EdgeType[]>([]);
  const [userStats, setUserStats] = useState<any>(null);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openRemovedModal, setOpenRemovedModal] = useState(false);
  const [openScheduling, setOpenScheduling] = useState(false);
  const [openConnectModal, setOpenConnectModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'CONNECT' | 'REMOVE' | null>(null);
  const {
    connections,
    loading,
    error,
    refresh: refreshConnections,
  } = useUserConnections(selectedUserId, loggedUserId);

  const {
    relationship,
    loading: loadingRelationship,
    error: relationshipError,
    refresh: refreshRelationship,
  } = useUserRelationship(loggedUserId, selectedUserId);

  if (isDev) {
    console.log('[UserActionsMenu] Connections:', connections);
    console.log('[UserActionsMenu] Relationship:', relationship);
    console.log('[UserActionsMenu] User Edges:', edges);
    console.log('[UserActionsMenu] Relationship Status:', relationship?.status);
    console.log('[UserActionsMenu] Relationship Level:', relationship?.level);
  }

  const isOwnProfile = loggedUserId === selectedUserId;
  // Função para recarregar todos os dados usando React Query invalidation
  const refreshData = async () => {
    if (loggedUserId && selectedUserId) {
      try {
        const [newEdges, newUserStats] = await Promise.all([
          getEdges(loggedUserId, selectedUserId),
          getUserStats(selectedUserId),
        ]);

        setEdges(newEdges);
        setSelectedEdges(newEdges.map((e: Edge) => e.type_code));
        setUserStats(newUserStats);

        // Invalidate React Query caches for related data
        queryClient.invalidateQueries({
          queryKey: ['userConnections', loggedUserId, selectedUserId],
        });
        queryClient.invalidateQueries({
          queryKey: ['userRelationship', loggedUserId, selectedUserId],
        });
        queryClient.invalidateQueries({ queryKey: ['userStats', selectedUserId] });

        refreshConnections();
        refreshRelationship();
      } catch (error) {
        if (isDev) console.error('Erro ao recarregar dados:', error);
      }
    }
  };

  // Removed forceRefreshConnections that used window.location.reload()
  // Now using React Query's invalidation for better performance

  useEffect(() => {
    getEdgeTypes().then(setEdgeTypes);
    if (loggedUserId && selectedUserId) {
      getEdges(loggedUserId, selectedUserId).then(edges => {
        setEdges(edges);
        setSelectedEdges(edges.map((e: Edge) => e.type_code));
      });
      getUserStats(selectedUserId).then(setUserStats);
    }
  }, [loggedUserId, selectedUserId]);

  // Recarrega os dados quando as conexões mudarem
  useEffect(() => {
    if (connections && !loading) {
      refreshData();
    }
  }, [connections?.relationship_status, connections?.level_code]);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEdgeAction = async (type: EdgeType, removeIfExists: boolean = true) => {
    const existingEdgeType = selectedEdges?.includes(type);

    if (existingEdgeType && removeIfExists) {
      setSelectedEdges(prev => prev.filter(e => e !== type));

      try {
        const existingEdge = edges?.find(e => e?.type_code === type);
        if (existingEdge) {
          await removeEdge(Number(existingEdge.id));
          setEdges(prev => prev.filter(e => e.id !== existingEdge.id));
        }
      } catch (error) {
        setSelectedEdges(prev => [...prev, type]);
        console.error('Failed to remove edge:', error);
        toastError('Erro ao remover ação');
      }
    } else if (!existingEdgeType) {
      setSelectedEdges(prev => [...prev, type]);

      try {
        const response = await createEdge(loggedUserId, selectedUserId, type);
        if (response) {
          const newEdge = response as Edge;
          setEdges(prev => [...prev, newEdge]);
          //toastSuccess('Ação criada com sucesso');
        }
      } catch (error) {
        setSelectedEdges(prev => prev.filter(e => e !== type));
        console.error('Failed to create edge:', error);
        toastError('Erro ao criar ação');
      }
    }
    // Recarrega os dados após a ação
    await refreshData();

    handleMenuClose();
  };

  const confirmPendingAction = async () => {
    if (pendingAction === 'CONNECT') {
      await handleEdgeAction('CONNECTION', true);
      setOpenConnectModal(false);
    } else if (pendingAction === 'REMOVE') {
      await confirmRemoveConnection();
      setOpenRemoveModal(false);
    }
    setPendingAction(null);
  };

  const hasConnection = connections?.level && connections?.level > 0;
  const totals = userStats?.edge_type_totals || {};

  // Estados da conexão
  const isPendingConnection = connections?.relationship_status === 'PENDING';
  const isConnectionInitiator = connections?.relationship_initiator === loggedUserId;
  const isConnectionReceiver = isPendingConnection && !isConnectionInitiator;
  const isConnectionRequester = isPendingConnection && isConnectionInitiator;
  const isConnected = connections?.level_code === 'CONNECTION';

  // Estados das ações baseado no array actions
  const userActions = connections?.actions || [];
  const hasApplause = userActions.includes('APPLAUSE');
  const hasLike = userActions.includes('LIKE');
  const hasDislike = userActions.includes('DISLIKE');

  const hasEdge = (type: EdgeType) => {
    return selectedEdges?.includes(type);
  };

  const handleConnectWithConfirmation = () => {
    setOpenConnectModal(true);
  };

  const confirmConnect = async () => {
    await handleEdgeAction('CONNECTION', true);
    setOpenConnectModal(false);
  };

  const edgeActions: EdgeAction[] = [
    {
      type: 'FAVORITED',
      icon: <FavoriteBorderIcon sx={{ color: isOwnProfile ? 'gray' : 'red' }} />,
      iconActive: <IoIosHeartDislike color={isOwnProfile ? 'gray' : 'red'} size={20} />,
      label: 'Favoritar',
      tooltip: 'Favoritar',
      variant: 'primary',
      show: true,
    },
    {
      type: 'CONNECTION',
      icon: <PersonAddAlt1Icon />,
      iconActive: <IoPersonRemove />,
      label: 'Conectar',
      tooltip: 'Conectar',
      variant: 'primary',
      show: !isConnected,
    },
    {
      type: 'SCHEDULED',
      icon: <CalendarMonthIcon />,
      label: 'Agendar',
      tooltip: 'Agendar',
      variant: 'outline',
      show: true,
    },
    {
      type: 'NETWORK',
      icon: <HubIcon />,
      label: 'Ver Rede Individual',
      tooltip: 'Ver Rede Individual',
      variant: 'outline',
      show: false,
    },
  ];

  const handleRemoveConnection = async () => {
    setOpenRemoveModal(true);
    handleMenuClose();
  };

  const confirmRemoveConnection = async () => {
    const edge = edges.find((e: any) => e.type_code === 'CONNECTION');
    setOpenRemoveModal(false);
    if (edge) {
      try {
        const response = await removeEdge(Number(edge.id));
        if (response.status === 200) {
          await refreshData();
          setOpenRemovedModal(true);
        } else {
          toastError('Erro ao remover conexão.');
        }
      } catch (error) {
        toastError('Erro ao remover conexão.');
      }
    } else {
      toastError('Conexão não encontrada.');
    }
  };

  const getActionLabel = (action: EdgeAction) => {
    if (action.type === 'FAVORITED') {
      return hasEdge('FAVORITED' as EdgeType) ? 'Remover Favorito' : 'Favoritar';
    }
    if (action.type === 'CONNECTION') {
      if (isConnectionReceiver) {
        return 'Aceitar Conexão';
      }
      if (isConnectionRequester) {
        return 'Aguardando Resposta';
      }
      if (hasEdge('CONNECTION' as EdgeType)) {
        return 'Remover Conexão';
      }
      return 'Conectar';
    }
    return action.label;
  };

  const getActionIcon = (action: EdgeAction): React.ReactElement => {
    if (action.type === 'FAVORITED' && hasEdge('FAVORITED' as EdgeType)) {
      return action.iconActive || action.icon;
    }
    if (action.type === 'CONNECTION') {
      if (isConnectionReceiver) {
        return <IoCheckmarkCircle color="green" size={20} />;
      }
      if (isConnectionRequester) {
        return <IoTime size={20} />;
      }
      if (hasEdge('CONNECTION' as EdgeType)) {
        return action.iconActive || action.icon;
      }
    }
    return action.icon;
  };

  const getActionVariant = (action: EdgeAction) => {
    if (action.type === 'FAVORITED' && hasEdge('FAVORITED' as EdgeType)) {
      return 'outline';
    }
    if (action.type === 'CONNECTION') {
      if (isConnectionReceiver) {
        return 'primary';
      }
      if (isConnectionRequester) {
        return 'outline';
      }
      if (hasEdge('CONNECTION' as EdgeType)) {
        return 'outline';
      }
    }
    return action.variant;
  };

  const getActionTooltip = (action: EdgeAction) => {
    if (action.type === 'FAVORITED') {
      return hasEdge('FAVORITED' as EdgeType) ? 'Remover Favorito' : 'Favoritar';
    }
    if (action.type === 'CONNECTION') {
      if (isConnectionReceiver) {
        return 'Aceitar solicitação de conexão';
      }
      if (isConnectionRequester) {
        return 'Aguardando resposta da solicitação';
      }
      if (hasEdge('CONNECTION' as EdgeType)) {
        return 'Remover Conexão';
      }
      return 'Conectar';
    }
    if (action.type === 'SCHEDULED') {
      if (!hasConnection) {
        return 'Necessário ser uma conexão para efetuar agendamentos com o usuário';
      }
      return 'Agendar';
    }
    return action.tooltip;
  };

  const handleActionClick = (action: EdgeAction) => {
    // Desabilita todas as ações se o usuário estiver visualizando seu próprio perfil
    if (isOwnProfile) {
      toastInfo('Você não pode realizar ações em seu próprio perfil.');
      return;
    }

    if (action.type === 'SCHEDULED') {
      if (!hasConnection) {
        toastInfo('Necessário ter conexão para agendamento.');
        return;
      }
      setOpenScheduling(true);
    } else if (action.type === 'CONNECTION') {
      if (isConnectionReceiver) {
        handleEdgeAction('CONNECTION', true);
      } else if (isConnectionRequester) {
        toastInfo('Aguardando resposta da solicitação de conexão.');
        return;
      } else if (hasEdge('CONNECTION' as EdgeType)) {
        handleRemoveConnection();
      } else {
        handleConnectWithConfirmation();
      }
    } else if (action.type === 'NETWORK') {
      // TODO:Implementar visualização da rede individual
      toastInfo('Funcionalidade em desenvolvimento');
    } else {
      handleEdgeAction(action.type as EdgeType, true);
    }
  };

  return (
    <>
      <Stack spacing={1} sx={{ minWidth: '200px' }}>
        {edgeActions
          .filter(action => action.show)
          .map(action => {
            const isSchedule = action.type === 'SCHEDULED';
            const isConnection = action.type === 'CONNECTION';
            const disabled =
              (isSchedule && !hasConnection) ||
              isOwnProfile ||
              (isConnection && isConnectionRequester);

            return (
              <Tooltip
                key={action.type}
                title={
                  isOwnProfile
                    ? 'Você não pode realizar ações em seu próprio perfil'
                    : getActionTooltip(action)
                }
                arrow
                placement="left"
              >
                <span>
                  <Button
                    size="medium"
                    variant={getActionVariant(action)}
                    icon={getActionIcon(action)}
                    onClick={() => handleActionClick(action)}
                    disabled={disabled}
                    fullWidth
                    style={{
                      paddingTop: '0.5rem',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    {getActionLabel(action)}
                  </Button>
                </span>
              </Tooltip>
            );
          })}

        <Button
          size="medium"
          variant="outline"
          icon={<MoreHorizIcon />}
          onClick={handleMenuOpen}
          disabled={isOwnProfile}
          fullWidth
          style={{
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
          }}
        >
          Mais opções
        </Button>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            background: theme.background.primary,
            right: 0,
          },
        }}
      >
        {hasConnection && !isOwnProfile && (
          <MenuItem onClick={handleRemoveConnection}>
            <ListItemText>Remover Conexão ({totals.CONNECTION ?? 0})</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleEdgeAction('APPLAUSE', true)} disabled={isOwnProfile}>
          <ListItemText>
            {hasApplause ? 'Remover Aplauso' : 'Aplaudir'} ({totals.APPLAUSE ?? 0})
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEdgeAction('LIKE', true)} disabled={isOwnProfile}>
          <ListItemText>
            {hasLike ? 'Remover Curtida' : 'Curtir'} ({totals.LIKE ?? 0})
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEdgeAction('DISLIKE', true)} disabled={isOwnProfile}>
          <ListItemText>
            {hasDislike ? 'Remover Não Curtida' : 'Não Curtir'} ({totals.DISLIKE ?? 0})
          </ListItemText>
        </MenuItem>
      </Menu>

      <GlobalModal
        open={openScheduling}
        title="Agendar Reunião"
        onClose={() => setOpenScheduling(false)}
        width="32rem"
        height="auto"
        keepMounted
      >
        <Scheduling
          requesterId={loggedUserId}
          hostId={selectedUserId}
          onClose={() => setOpenScheduling(false)}
          hostName={`${hostName}`}
        />
      </GlobalModal>
      <GlobalModal
        open={openRemoveModal}
        title="CONFIRMAÇÃO"
        description="Deseja realmente remover conexão com esse membro?"
        onClose={() => setOpenRemoveModal(false)}
        onCancel={() => setOpenRemoveModal(false)}
        onConfirm={confirmRemoveConnection}
        cancelButtonText="Cancelar"
        confirmButtonText="Bloquear"
        width="32rem"
        height="auto"
      />
      <GlobalModal
        open={openRemovedModal}
        title="CONFIRMAÇÃO"
        description={'Conexão desfeita! Este membro não está mais conectado a você.'}
        onClose={() => setOpenRemovedModal(false)}
        onCancel={undefined}
        onConfirm={() => setOpenRemovedModal(false)}
        cancelButtonText={undefined}
        confirmButtonText="Fechar"
        width="32rem"
        height="auto"
      />
      <GlobalModal
        open={openConnectModal}
        title="CONFIRMAÇÃO"
        description="Deseja realmente se conectar com esse membro?"
        onClose={() => setOpenConnectModal(false)}
        onCancel={() => setOpenConnectModal(false)}
        onConfirm={confirmConnect}
        cancelButtonText="Cancelar"
        confirmButtonText="Conectar"
        width="32rem"
        height="auto"
      />
    </>
  );
};

export default UserActionsMenu;
